import type { LucideIcon } from 'lucide-react';
import { ClipboardList, FileText, Presentation } from 'lucide-react';

/** Tipo de recurso — paso 1 del wizard de creación global. */
export type StudioCreateResourceType = 'guide' | 'rubric' | 'presentation';

export interface StudioCreateResourceOption {
  id: StudioCreateResourceType;
  title: string;
  subtitle: string;
  Icon: LucideIcon;
  accent: 'accent' | 'mint';
}

export const STUDIO_CREATE_RESOURCE_OPTIONS: StudioCreateResourceOption[] = [
  {
    id: 'guide',
    title: 'Guía',
    subtitle: 'Ficha imprimible con bloques pedagógicos y práctica',
    Icon: FileText,
    accent: 'accent',
  },
  {
    id: 'rubric',
    title: 'Rúbrica',
    subtitle: 'Criterios de evaluación alineados al curso',
    Icon: ClipboardList,
    accent: 'accent',
  },
  {
    id: 'presentation',
    title: 'Presentación',
    subtitle: 'Clase interactiva para proyectar en la Consola',
    Icon: Presentation,
    accent: 'mint',
  },
];

/** Perfiles de flujo — conectan con GuideGenerationRouter / SessionIntentContract. */
export type StudioFlowProfile =
  | 'scale_project'
  | 'measurement_inquiry'
  | 'workshop_scaffold'
  | 'skill_drill'
  | 'source_inquiry'
  | 'generic';

export interface StudioFlowProfileOption {
  id: StudioFlowProfile;
  /** Emoji o pictograma didáctico (no ID técnico). */
  icon: string;
  label: string;
  description: string;
}

export const STUDIO_FLOW_PROFILE_OPTIONS: StudioFlowProfileOption[] = [
  {
    id: 'scale_project',
    icon: '📐',
    label: 'Proyecto a escala',
    description: 'Diseñar un plano o maqueta partiendo de medidas reales',
  },
  {
    id: 'measurement_inquiry',
    icon: '📏',
    label: 'Explorar midiendo',
    description: 'Observar el entorno, registrar datos y comparar resultados',
  },
  {
    id: 'source_inquiry',
    icon: '📜',
    label: 'Analizar una fuente',
    description: 'Lectura cercana de documento histórico y plantilla de análisis',
  },
  {
    id: 'workshop_scaffold',
    icon: '🛠️',
    label: 'Taller guiado',
    description: 'Ver un ejemplo, practicar con apoyo y transferir solos',
  },
  {
    id: 'skill_drill',
    icon: '🎯',
    label: 'Practicar una destreza',
    description: 'Ensayos cortos y focalizados, con cierre rápido',
  },
  {
    id: 'generic',
    icon: '✨',
    label: 'Secuencia libre',
    description: 'Flujo adaptable sin plantilla fija de dominio',
  },
];

export const FLOW_PROFILE_TO_ARCHETYPE = {
  scale_project: 'taller',
  measurement_inquiry: 'indagacion',
  source_inquiry: 'indagacion',
  workshop_scaffold: 'taller',
  skill_drill: 'destreza',
  /** Secuencia libre: sin arquetipo forzado — el prompt decide (destreza vs indagación). */
  generic: null,
} as const satisfies Record<
  StudioFlowProfile,
  'destreza' | 'indagacion' | 'taller' | 'evaluacion' | null
>;

/** Perfiles de medición/escala — solo tienen sentido en matemática. */
export const MATH_ONLY_FLOW_PROFILES = new Set<StudioFlowProfile>([
  'scale_project',
  'measurement_inquiry',
]);

/** Perfiles de indagación documental — Historia. */
export const HISTORIA_ONLY_FLOW_PROFILES = new Set<StudioFlowProfile>(['source_inquiry']);

const FLOW_PROFILE_IDS = new Set<string>(STUDIO_FLOW_PROFILE_OPTIONS.map((o) => o.id));

export function isStudioFlowProfile(value: string | null | undefined): value is StudioFlowProfile {
  return Boolean(value && FLOW_PROFILE_IDS.has(value));
}

/** Opciones didácticas visibles según asignatura del curso. */
export function flowProfileOptionsForSubject(subjectTag?: string | null): StudioFlowProfileOption[] {
  const tag = String(subjectTag || '')
    .toLowerCase()
    .trim();
  if (!tag || tag === 'general') {
    return STUDIO_FLOW_PROFILE_OPTIONS;
  }
  if (tag === 'matematica') {
    return STUDIO_FLOW_PROFILE_OPTIONS.filter((option) => !HISTORIA_ONLY_FLOW_PROFILES.has(option.id));
  }
  if (tag === 'historia') {
    return STUDIO_FLOW_PROFILE_OPTIONS.filter((option) => !MATH_ONLY_FLOW_PROFILES.has(option.id));
  }
  return STUDIO_FLOW_PROFILE_OPTIONS.filter(
    (option) => !MATH_ONLY_FLOW_PROFILES.has(option.id) && !HISTORIA_ONLY_FLOW_PROFILES.has(option.id),
  );
}
